import { useTranslation } from 'react-i18next';
import { Input, Label } from '@monksflow/monks-ui';

interface AddAccountPlatformFieldsProps {
  needsAdvertiser: boolean;
  advertiserId: string;
  onAdvertiserIdChange: (value: string) => void;
  platformLabel: string;
  usesGoogleAdsApi: boolean;
  loginCustomerId: string;
  onLoginCustomerIdChange: (value: string) => void;
  asksForCm360Account: boolean;
  cm360AccountId: string;
  onCm360AccountIdChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Platform-conditional identifiers: the advertiser id a platform like DV360
 * addresses everything through (see `PLATFORMS_REQUIRING_AN_ADVERTISER`), the
 * Google Ads manager account id an agency reaches a client through, and the
 * CM360 account a Google login reaching several has to name.
 */
export function AddAccountPlatformFields({
  needsAdvertiser,
  advertiserId,
  onAdvertiserIdChange,
  platformLabel,
  usesGoogleAdsApi,
  loginCustomerId,
  onLoginCustomerIdChange,
  asksForCm360Account,
  cm360AccountId,
  onCm360AccountIdChange,
  disabled,
}: AddAccountPlatformFieldsProps) {
  const { t } = useTranslation('knowledge');

  return (
    <>
      {needsAdvertiser && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-0.5">
            <Label htmlFor="sheet-advertiser-id">
              {t('mediaConnectors.addAccountDialog.advertiserId')}
            </Label>
            <span aria-hidden className="text-destructive">
              *
            </span>
          </div>
          <Input
            id="sheet-advertiser-id"
            value={advertiserId}
            onChange={(event) => onAdvertiserIdChange(event.target.value)}
            placeholder="907583025"
            disabled={disabled}
            required
          />
          <p className="text-xs text-muted-foreground">
            {t('mediaConnectors.addAccountDialog.advertiserIdHelp', { platform: platformLabel })}
          </p>
        </div>
      )}

      {usesGoogleAdsApi && (
        <div className="space-y-1.5">
          <Label htmlFor="sheet-login-customer-id">
            {t('mediaConnectors.addAccountDialog.managerAccountId')}
          </Label>
          <Input
            id="sheet-login-customer-id"
            value={loginCustomerId}
            onChange={(event) => onLoginCustomerIdChange(event.target.value)}
            placeholder="123-456-7890"
            disabled={disabled}
          />
          <p className="text-xs text-muted-foreground">
            {t('mediaConnectors.addAccountDialog.managerAccountIdHelp')}
          </p>
        </div>
      )}

      {asksForCm360Account && (
        <div className="space-y-1.5">
          <Label htmlFor="sheet-cm360-account-id">
            {t('mediaConnectors.addAccountDialog.cm360AccountId')}
          </Label>
          <Input
            id="sheet-cm360-account-id"
            value={cm360AccountId}
            onChange={(event) => onCm360AccountIdChange(event.target.value)}
            placeholder="1234567"
            disabled={disabled}
          />
          <p className="text-xs text-muted-foreground">
            {t('mediaConnectors.addAccountDialog.cm360AccountIdHelp')}
          </p>
        </div>
      )}
    </>
  );
}

AddAccountPlatformFields.displayName = 'AddAccountPlatformFields';
